/**
 * Billing Type Definitions
 * Types for billing, subscriptions, plans, and entitlements
 */

// ============================================================================
// Trial Status Types
// ============================================================================

export interface TrialStatus {
  isTrialing: boolean;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  daysRemaining: number;
  isExpired: boolean;
}

// ============================================================================
// Plan Types
// ============================================================================

export type PlanInterval = 'monthly' | 'yearly';

export interface PlanFeature {
  key: string;
  label: string;
  included: boolean;
  limit?: number;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  amount: number;              // Amount in cents
  currency: string;            // e.g., "USD"
  interval: PlanInterval;
  features: PlanFeature[];
  trialPeriodDays: number;
  isPopular?: boolean;
  sortOrder: number;
}

// ============================================================================
// Subscription Types
// ============================================================================

/**
 * Every status billing can store (services/billing/app/models/subscription.py
 * SubscriptionStatus). GET /billing/subscription returns the tenant's CURRENT
 * subscription (WP-BILLING-20), so past_due, unpaid and paused reach the UI;
 * canceled / incomplete_expired / expired are terminal and are not returned
 * there, but other endpoints may carry them.
 */
export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'trialing'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused'
  | 'expired';           // FlowIO-only: trial ended without converting

/** Stripe could not collect payment: show the payment-failed UI and the portal, never checkout. */
export const PAYMENT_FAILED_STATUSES: readonly SubscriptionStatus[] = ['past_due', 'unpaid'];

export interface Subscription {
  id: string;
  planId: string;
  planName: string;
  status: SubscriptionStatus;
  // WP-BUG-BILLING-SYNC-2: Backend returns null for legacy subscriptions
  currentPeriodStart: string | null;  // ISO date string or null
  currentPeriodEnd: string | null;    // ISO date string or null
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  trialStart: string | null;
  trialEnd: string | null;
  // WP-BUG-BILLING-SYNC-2: These field names match backend SubscriptionWithPlanResponse
  planAmount: number;          // Amount in cents (was: amount)
  planCurrency: string;        // Currency code (was: currency)
  planInterval: PlanInterval;  // Billing interval (was: interval)
  // WP-BILLING-19: granted by a FlowIO coupon, not paid through Stripe. There is
  // no Stripe portal for it (the portal 404s); changes go through support.
  isCouponBased: boolean;
}

/**
 * WP-BILLING-19: the API refuses a second purchase for a household that
 * already has a subscription (409, code SUBSCRIPTION_EXISTS). Callers route to
 * managing the existing subscription instead of showing a raw error.
 */
export const SUBSCRIPTION_EXISTS_CODE = 'SUBSCRIPTION_EXISTS';

export function isSubscriptionExistsError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === SUBSCRIPTION_EXISTS_CODE
  );
}

// ============================================================================
// Entitlement Types
// ============================================================================

export type EntitlementSource = 'subscription' | 'addon' | 'promo' | 'manual';

export interface Entitlement {
  id: string;
  featureKey: string;
  source: EntitlementSource;
  grantedAt: string;
  expiresAt: string | null;
  isActive: boolean;
  limit?: number;
  used?: number;
}

// ============================================================================
// Coupon Types
// ============================================================================

export type CouponDiscountType =
  | 'percent'
  | 'fixed'
  | 'percent_off'      // Backend value for percentage discount
  | 'amount_off'       // Backend value for fixed amount discount
  | 'free_forever'     // WP-BILLING-11: Grants premium access without payment
  | 'free_months'      // Free months of subscription
  | 'extended_trial';  // Extended trial period

export interface CouponValidation {
  valid: boolean;
  code: string;
  discountType: CouponDiscountType | null;
  discountValue: number | null;      // Percentage (0-100) or fixed amount in cents
  discountDisplay: string | null;    // Human-readable discount (e.g., "20% off", "$10 off")
  errorMessage: string | null;
  expiresAt: string | null;
  duration: string | null;           // How long discount applies (once, repeating, forever)
  durationInMonths: number | null;   // Months for repeating duration
  remainingUses: number | null;      // Remaining uses (null if unlimited)
}

// ============================================================================
// Checkout Types
// ============================================================================

/**
 * Checkout session response.
 *
 * WP-BILLING-11: This response now supports two modes:
 * 1. Stripe checkout: checkoutUrl, sessionId, expiresAt are present
 * 2. Instant activation (FREE_FOREVER): activated=true, subscriptionId, message are present
 *
 * Frontend should check `activated` first - if true, don't redirect to Stripe.
 */
export interface CheckoutSession {
  // Stripe checkout fields (optional - only present when redirecting to Stripe)
  checkoutUrl?: string;              // Stripe Checkout URL to redirect to
  sessionId?: string;                // Stripe Session ID
  expiresAt?: string | number;       // When session expires (ISO string or Unix timestamp)

  // Instant activation fields (optional - only present for FREE_FOREVER)
  activated?: boolean;               // True if subscription was instantly activated
  subscriptionId?: string;           // Created subscription ID (present when activated=true)
  message?: string;                  // User-facing success message
}

export interface CreateCheckoutRequest {
  planId: string;
  successUrl: string;
  cancelUrl: string;
  couponCode?: string;
}

// ============================================================================
// Cancellation Types
// ============================================================================

export interface CancelSubscriptionRequest {
  immediately?: boolean;
  reason?: string;
}

export interface CancelSubscriptionResponse {
  subscription: Subscription;
  message: string;
}

// ============================================================================
// Payment History Types
// ============================================================================

/**
 * WP-BUG-BILLING-SYNC-2: Invoice status from Stripe.
 * Maps to Stripe's invoice.status field.
 */
export type InvoiceStatus = 'paid' | 'open' | 'void' | 'draft' | 'uncollectible' | 'unknown';

/**
 * Invoice history item from Stripe.
 *
 * WP-BUG-BILLING-SYNC-2: This matches the backend's InvoiceHistoryItem schema.
 * Uses Stripe invoice IDs (strings) not UUIDs.
 */
export interface Payment {
  id: string;                           // Stripe invoice ID (e.g., in_1T0AzP...)
  amount: number;                       // Amount in cents
  currency: string;                     // Currency code (e.g., 'usd')
  status: InvoiceStatus;                // Invoice status from Stripe
  invoicePdf: string | null;            // URL to download invoice PDF
  createdAt: string;                    // ISO date string
  periodStart: string | null;           // Billing period start
  periodEnd: string | null;             // Billing period end
}

export interface PaymentHistoryResponse {
  payments: Payment[];
  hasMore: boolean;
  nextCursor: string | null;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface PlansResponse {
  plans: Plan[];
}

export interface SubscriptionResponse {
  subscription: Subscription | null;
  hasActiveSubscription: boolean;
}

export interface EntitlementsResponse {
  entitlements: Entitlement[];
}
